// File: /js/shared/components/ecgIndicator.js
// The ECG selection indicator — rescos_v2's visual signature (AD 2026-07-07,
// picked off the animated mockup comparator; replaces the legacy `{ }` bracket
// spans). Under the ACTIVE item of a nav/tab strip, a thin cardiac trace draws
// itself (stroke-dash animation) with a one-time pulse at the QRS apex, then
// stays as a static fine trace. Labels never move — the indicator is an
// absolutely-positioned overlay, which also fixes the historic bracket
// width-shift.
//
// Opt-in per strip via `data-ecg` on the ITEMS' positioned container:
//   data-ecg               enable (container must be positioned; the component
//                          sets position:relative as a fallback)
//   data-ecg-items="sel"   item selector inside the strip (default 'a, button')
//   data-ecg-active="cls"  the active class to watch (default 'active')
//   data-ecg-label="sel"   optional inner element to measure (e.g. a label
//                          span) so the trace hugs the text, not the padding
//
// The component OWNS no click logic: a MutationObserver re-syncs whenever the
// active class moves (the existing tab-switch / tablist handlers keep working
// untouched), a ResizeObserver re-places on layout changes AND first reveals
// hidden strips (the login modal draws its trace when it opens), and a
// debounced window resize re-places everything. `prefers-reduced-motion`
// renders the trace statically (styles in components/ecg-indicator.css).

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

// The "moniteur" geometry (AD 2026-07-07): the trace RIDES ON the strip's
// bottom rule as its isoelectric baseline, and the QRS complex rises UP into a
// clear band above — it no longer dips an undershoot THROUGH the line (the old
// path's S wave reached ~1.5px below the item edge and collided with the menu
// bar's border). So the strip's own line comes alive as a heartbeat under the
// active item. The band above the line is what the menu gives the pulse via
// extra bottom padding (menu.css); tabs already carry enough room.
const SVG_H = 18;        // viewBox height (a touch taller — headroom for the R spike)
const BASE_Y = 15;       // isoelectric baseline WITHIN the viewBox (low, near the floor)
const BASE_ABOVE = 1;    // baseline sits 1px above the item's bottom edge → ON the rule
const MIN_W = 56;        // shortest trace (tiny labels still read as an ECG)
const SPIKE_HALF = 8.25; // half-width of the QRS complex block (centres it under the label)
const APEX_DX = 12;      // x of the R apex within the complex (from its start)
const APEX_Y = 4.5;      // y of the R apex (anchors the one-time pulse dot)

function pathD(w) {
  const a = Math.max(2, w / 2 - SPIKE_HALF);
  // flat baseline → P bump → tiny Q → R spike UP → shallow S (kisses baseline) → flat
  return `M0,${BASE_Y} H${a} l3,-2.5 l3,2.5 l2,0 l1.5,1.5 l2.5,-12 l3,12 l1.5,-1.5 H${w}`;
}

class EcgStrip {
  constructor(strip) {
    this.strip = strip;
    this.itemsSel = strip.dataset.ecgItems || 'a, button';
    this.activeCls = strip.dataset.ecgActive || 'active';
    this.labelSel = strip.dataset.ecgLabel || '';
    this.active = null;
    this.revealed = false;

    if (getComputedStyle(strip).position === 'static') {
      strip.style.position = 'relative';
    }

    this.ind = document.createElement('div');
    this.ind.className = 'ecg-ind';
    this.ind.setAttribute('aria-hidden', 'true');
    this.ind.innerHTML =
      `<svg height="${SVG_H}" focusable="false"><path/><circle cy="${APEX_Y}" r="2.5"/></svg>`;
    strip.appendChild(this.ind);

    // ⚠ The observer watches the WHOLE strip subtree — which contains the
    // indicator this component itself mutates (is-ready / is-beat). Records
    // originating inside the indicator MUST be ignored: reacting to our own
    // class writes re-enters sync(), whose class writes re-fire the observer —
    // an infinite microtask loop that starves the main thread BEFORE first
    // paint (blank page site-wide; shipped 30e11cc, hotfixed same day).
    this.mo = new MutationObserver((records) => {
      if (records.every((r) => this.ind.contains(r.target))) return;
      this.sync(true);
    });
    this.mo.observe(strip, { subtree: true, attributes: true, attributeFilter: ['class'] });

    this.ro = new ResizeObserver(() => this.onResize());
    this.ro.observe(strip);

    this.sync(false);
  }

  activeItem() {
    const items = this.strip.querySelectorAll(this.itemsSel);
    for (const it of items) {
      if (it.classList.contains(this.activeCls)) return it;
    }
    return null;
  }

  /* Reposition; animate only when the active item genuinely changed. */
  sync(mayAnimate) {
    const it = this.activeItem();
    if (!it) {
      this.ind.classList.remove('is-ready');
      this.active = null;
      return;
    }
    const changed = it !== this.active;
    this.active = it;
    if (!this.place()) {
      // Geometry is unusable right now (hidden strip, or a transform-animated
      // ancestor mid-flight — see place()). Don't reveal an unplaced trace;
      // retry once the geometry is truthful.
      this.retryPlace(mayAnimate);
      return;
    }
    if (this.retriesLeft != null) {
      // Placed on the tail of a retry episode: the tolerance admits the last
      // ~scale(0.998) animation frames, leaving a ≈1 px offset. Keep confirming
      // (idempotent, transition-smoothed) until two frames agree.
      this.retriesLeft = null;
      this.confirmPlace();
    }
    if (this.revealed) {
      // Re-assert visibility (an active-less interval removes is-ready).
      this.ind.classList.add('is-ready');
      if (mayAnimate && changed) this.animate();
    } else {
      this.reveal();
    }
  }

  /* Hidden strips (the login modal) measure 0×0 — reveal + first draw happen
     when the ResizeObserver reports real geometry. */
  onResize() {
    const hasSize = this.strip.offsetWidth > 0;
    if (hasSize && !this.revealed) {
      this.sync(false);
      return;
    }
    if (hasSize) this.place();
  }

  /* First appearance = the page-load signature: fade in AND draw the trace. */
  reveal() {
    if (this.revealed || !this.active || this.strip.offsetWidth === 0) return;
    this.revealed = true;
    requestAnimationFrame(() => {
      this.ind.classList.add('is-ready');
      this.animate();
    });
  }

  /* Write the indicator's geometry. Returns false when it can't be trusted:
     a 0-width strip (hidden), or a strip whose rect width disagrees with its
     LAYOUT width — a transform-animated ancestor mid-flight. The login popup
     opens via `fadeInScale` (scale(0)→1, popup.css): the ResizeObserver fires
     on the display flip while the popup is still near scale(0), so offsetWidth
     is real but every getBoundingClientRect is collapsed — placing then wrote
     an empty trace that only appeared (flying in from the strip origin) on the
     next tab click (observed 2026-08-16). */
  place() {
    const it = this.active;
    if (!it) return false;
    const box = (this.labelSel && it.querySelector(this.labelSel)) || it;
    const sr = this.strip.getBoundingClientRect();
    const br = box.getBoundingClientRect();
    const ir = it.getBoundingClientRect();
    if (sr.width === 0) return false;
    if (Math.abs(sr.width - this.strip.offsetWidth) > 1.5) return false;

    const w = Math.max(MIN_W, Math.round(br.width) + 10);
    // scrollLeft/Top: inside a scrollable strip (.tab-scroll) the indicator
    // lives in CONTENT coordinates and must scroll with the items.
    const x = br.left - sr.left + this.strip.scrollLeft + (br.width - w) / 2;
    // Drop the baseline onto the item's bottom edge (= the strip's rule): the
    // isoelectric line sits BASE_Y down the viewBox, so the box top goes an
    // extra BASE_ABOVE above that edge.
    const y = ir.bottom - sr.top + this.strip.scrollTop - BASE_ABOVE - BASE_Y;

    this.ind.style.transform = `translate(${x}px, ${y}px)`;
    this.ind.style.width = `${w}px`;

    const svg = this.ind.firstElementChild;
    const path = svg.querySelector('path');
    const dot = svg.querySelector('circle');
    svg.setAttribute('width', w);
    svg.setAttribute('viewBox', `0 0 ${w} ${SVG_H}`);
    path.setAttribute('d', pathD(w));
    dot.setAttribute('cx', Math.max(2, w / 2 - SPIKE_HALF) + APEX_DX);
    dot.setAttribute('cy', APEX_Y);
    return true;
  }

  /* One rAF-paced retry while a visible strip's geometry is distorted (the
     popup's 300 ms open animation ≈ 20 frames): re-run sync() next frame; its
     failure path re-enters retryPlace(), its success path ends the loop. Stops
     while hidden (the ResizeObserver restarts the cycle on the next reveal)
     and after ~2 s as a safety net against a permanently-scaled ancestor. */
  retryPlace(mayAnimate) {
    if (this.retrying) return;
    if (this.retriesLeft == null) this.retriesLeft = 120;
    this.retrying = true;
    requestAnimationFrame(() => {
      this.retrying = false;
      if (this.strip.offsetWidth === 0 || this.retriesLeft-- <= 0) {
        this.retriesLeft = null;
        return;
      }
      this.sync(mayAnimate);
    });
  }

  /* Re-place each frame until the written geometry stops moving (two
     consecutive frames agree) — rides out the remainder of an ancestor's
     open animation. Bounded to ~30 frames. */
  confirmPlace() {
    let frames = 30;
    const tick = () => {
      const before = this.ind.style.transform + this.ind.style.width;
      const ok = this.place();
      const same = ok && (this.ind.style.transform + this.ind.style.width) === before;
      if (!same && frames-- > 0) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  /* The signature: draw the trace + pulse once at the apex. */
  animate() {
    if (REDUCED.matches || !this.active || !this.revealed) return;
    const path = this.ind.querySelector('path');
    const len = path.getTotalLength();
    path.style.transition = 'none';
    path.style.strokeDasharray = len;
    path.style.strokeDashoffset = len;
    void path.getBoundingClientRect();
    path.style.transition = 'stroke-dashoffset .5s ease-out .1s';
    path.style.strokeDashoffset = '0';
    this.ind.classList.remove('is-beat');
    void this.ind.offsetWidth;
    this.ind.classList.add('is-beat');
  }
}

export function initEcgIndicators() {
  const strips = [...document.querySelectorAll('[data-ecg]')].map((s) => new EcgStrip(s));
  if (!strips.length) return;
  let t;
  window.addEventListener('resize', () => {
    clearTimeout(t);
    t = setTimeout(() => strips.forEach((s) => s.sync(false)), 120);
  });
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => strips.forEach((s) => s.sync(false)));
  }
}
